import React, { useEffect, useRef, useState, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Plus, Maximize2 } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';

const POLAND_CENTER = { lat: 52.237049, lng: 19.2 };
const DEFAULT_ZOOM = 6;

const farmSignature = (farms) =>
  (farms || [])
    .map((f) => `${f._id}:${f.location?.latitude},${f.location?.longitude}:${f.farmName || ''}`)
    .sort()
    .join('|');

/**
 * Mapa Google z pinezkami farm — memoizowana, bez zbędnego remountu.
 */
const FarmMap = memo(function FarmMap({ farms = [], loading = false, height = 420, showAddButton = true }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const lastSignatureRef = useRef('');
  const farmsRef = useRef(farms);
  farmsRef.current = farms;

  const [mapReady, setMapReady] = useState(false);
  const [mapType, setMapType] = useState('roadmap');
  const [error, setError] = useState(null);

  const signature = useMemo(() => farmSignature(farms), [farms]);
  const farmCount = useMemo(
    () => farms.filter((f) => f.location?.latitude != null).length,
    [farms]
  );

  const clearMarkers = () => {
    markersRef.current.forEach((m) => {
      try {
        m.setMap(null);
      } catch (_) { /* ignore */ }
    });
    markersRef.current = [];
  };

  const placeMarkers = (map, farmList, { fit = false } = {}) => {
    clearMarkers();
    if (!window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    let added = 0;

    farmList.forEach((farm) => {
      const lat = farm.location?.latitude;
      const lng = farm.location?.longitude;
      if (lat == null || lng == null) return;

      const position = { lat: Number(lat), lng: Number(lng) };
      const marker = new window.google.maps.Marker({
        position,
        map,
        title: farm.farmName,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: '#e85d5d',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        }
      });

      const info = new window.google.maps.InfoWindow({
        content: `<div style="font-family:system-ui,sans-serif;padding:4px 2px;min-width:120px">
          <strong style="font-size:13px">${farm.farmName || 'Farma'}</strong>
          <div style="font-size:11px;color:#666;margin-top:4px">${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}</div>
        </div>`
      });

      marker.addListener('click', () => info.open({ map, anchor: marker }));
      markersRef.current.push(marker);
      bounds.extend(position);
      added += 1;
    });

    if (!fit) return;

    if (added === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(10);
    } else if (added > 1) {
      map.fitBounds(bounds, 48);
    } else {
      map.setCenter(POLAND_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    }
  };

  // Jednorazowa inicjalizacja mapy
  useEffect(() => {
    let cancelled = false;
    let retryTimer;

    const createMap = () => {
      if (cancelled || !mapRef.current || !window.google?.maps?.Map) return;
      if (mapInstanceRef.current) {
        setMapReady(true);
        return;
      }

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: DEFAULT_ZOOM,
        center: POLAND_CENTER,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_BOTTOM
        }
      });
      setMapReady(true);
    };

    const waitForApi = (attempt = 0) => {
      if (cancelled) return;
      if (window.google?.maps?.Map) {
        createMap();
        return;
      }
      if (attempt > 80) {
        setError('Nie udało się załadować mapy Google');
        return;
      }
      retryTimer = setTimeout(() => waitForApi(attempt + 1), 100);
    };

    if (window.google?.maps?.Map) {
      createMap();
      return () => {
        cancelled = true;
        clearTimeout(retryTimer);
      };
    }

    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      const onLoad = () => waitForApi();
      existing.addEventListener('load', onLoad);
      waitForApi();
      return () => {
        cancelled = true;
        clearTimeout(retryTimer);
        existing.removeEventListener('load', onLoad);
      };
    }

    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key || key === 'YOUR_API_KEY') {
      setError('Brak klucza Google Maps (REACT_APP_GOOGLE_MAPS_API_KEY)');
      return () => { cancelled = true; };
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => waitForApi();
    script.onerror = () => setError('Błąd ładowania Google Maps API');
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      clearMarkers();
      mapInstanceRef.current = null;
    };
  }, []);

  // Markery tylko przy zmianie danych farm
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    if (signature === lastSignatureRef.current) return;

    const firstPaint = !lastSignatureRef.current;
    lastSignatureRef.current = signature;
    placeMarkers(mapInstanceRef.current, farmsRef.current, { fit: firstPaint });
  }, [signature, mapReady]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;
    mapInstanceRef.current.setMapTypeId(
      mapType === 'satellite'
        ? window.google.maps.MapTypeId.HYBRID
        : window.google.maps.MapTypeId.ROADMAP
    );
  }, [mapType]);

  const showInitialLoader = loading && !mapReady && !error;

  return (
    <div className="card h-full flex flex-col overflow-hidden">
      <div className="card-header flex items-center justify-between gap-3 flex-wrap !py-3.5">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-5 w-5 text-accent-red flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-ink tracking-wide uppercase truncate">Mapa Polski</h3>
            <p className="text-xs text-ink-faint">
              {showInitialLoader
                ? 'Ładowanie…'
                : `${farmCount} ${farmCount === 1 ? 'farma' : farmCount < 5 ? 'farmy' : 'farm'} w systemie`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl bg-surface p-0.5 border border-black/[0.04]">
            <button
              type="button"
              onClick={() => setMapType('roadmap')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                mapType === 'roadmap' ? 'bg-white text-ink shadow-sm' : 'text-ink-faint hover:text-ink'
              }`}
            >
              Mapa
            </button>
            <button
              type="button"
              onClick={() => setMapType('satellite')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                mapType === 'satellite' ? 'bg-white text-ink shadow-sm' : 'text-ink-faint hover:text-ink'
              }`}
            >
              Satelita
            </button>
          </div>
          {showAddButton && (
            <Link
              to="/planner/farms"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Dodaj farmę
            </Link>
          )}
          <Link
            to="/planner/map"
            className="p-2 rounded-xl text-ink-faint hover:text-ink hover:bg-surface transition-colors"
            title="Pełny ekran mapy"
          >
            <Maximize2 className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="relative flex-1 min-h-0" style={{ height }}>
        {showInitialLoader && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/80">
            <LoadingSpinner size="md" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-surface px-6 text-center">
            <MapPin className="h-10 w-10 text-ink-faint mb-3" />
            <p className="text-sm font-medium text-ink">{error}</p>
            <Link to="/planner/map" className="mt-3 text-sm text-primary-600 hover:underline">
              Otwórz mapę w Planerze
            </Link>
          </div>
        )}
        {!loading && !error && farmCount === 0 && mapReady && (
          <div className="absolute inset-x-0 top-3 z-10 flex justify-center pointer-events-none">
            <div className="pointer-events-auto px-3 py-1.5 rounded-full bg-white/95 shadow-card text-xs text-ink-soft border border-black/[0.04]">
              Brak farm z lokalizacją —{' '}
              <Link to="/planner/farms" className="text-primary-600 font-semibold hover:underline">
                dodaj farmę
              </Link>
            </div>
          </div>
        )}
        <div ref={mapRef} className="absolute inset-0 w-full h-full bg-surface-muted" />
      </div>
    </div>
  );
}, (prev, next) => (
  prev.loading === next.loading &&
  prev.height === next.height &&
  prev.showAddButton === next.showAddButton &&
  farmSignature(prev.farms) === farmSignature(next.farms)
));

export default FarmMap;
